import { faAlarmClock } from '@fortawesome/pro-light-svg-icons/faAlarmClock';
import { faChartSimpleHorizontal } from '@fortawesome/sharp-thin-svg-icons/faChartSimpleHorizontal';
import { faCircleInfo } from '@fortawesome/sharp-thin-svg-icons/faCircleInfo';
import { faHeart } from '@fortawesome/sharp-thin-svg-icons/faHeart';
import { faThumbsUp } from '@fortawesome/sharp-thin-svg-icons/faThumbsUp';
import { faTruckFast } from '@fortawesome/sharp-thin-svg-icons/faTruckFast';
import { faUser } from '@fortawesome/sharp-thin-svg-icons/faUser';
import { CLOSE_TO_SEARCH, MY_DRAWN_AREA, MY_TRAVEL_TIME_AREA } from '@propertypal/shared/src/constants/search';
import useInView from '@propertypal/shared/src/hooks/useInView';
import useRecentSearches, { RecentSearchData } from '@propertypal/shared/src/hooks/useRecentSearches';
import useSearchState from '@propertypal/shared/src/hooks/useSearchState';
import { selectProperties } from '@propertypal/shared/src/reducers/properties';
import { getNlpUrl, selectSearch } from '@propertypal/shared/src/reducers/search';
import { trackGaEvent } from '@propertypal/shared/src/services/analytics';
import { Property } from '@propertypal/shared/src/types';
import { SuperFeatureProperty } from '@propertypal/shared/src/types/superFeature';
import puralise from '@propertypal/shared/src/utils/puralise';
import { Filters } from '@propertypal/shared/src/utils/search/getNlpFilters';
import ImageSlideshow from '@propertypal/web-ui/src/animation/ImageSlideshow';
import Button from '@propertypal/web-ui/src/buttons/Button';
import PopularPropertiesHome from '@propertypal/web-ui/src/home/PopularPropertiesHome';
import FontAwesomeIcon from '@propertypal/web-ui/src/icons/FontAwesomeIcon';
import { Benefits } from '@propertypal/web-ui/src/jumbotrons/NewHomesJumboTron.style';
import { Box } from '@propertypal/web-ui/src/layout';
import { MidHeading, SubHeading, Text } from '@propertypal/web-ui/src/typography';
import Link from 'next/link';
import router from 'next/router';
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTheme } from 'styled-components';
import IMAGE_LOCATOR, { IMAGE_LOCATOR_XLARGE } from '../../constants/imageLocator';
import SearchBar from '../search/SearchBar/SearchBar.pn';
import SearchPreviewModal from '../search/SearchPreviewModal';
import {
  HandPickedContainer,
  NewHomesContent,
  ShowHomeWrapper,
  ShowHomeContent,
  ShowHomeViewings,
  Tip,
} from './NewHomesLandingPage.style.pn';

interface NewHomesLandingPagePnProps extends RecentSearchData {
  images: string[];
  superfeatures: SuperFeatureProperty[];
  featured?: Property[];
  totalViewings?: number;
  loggedIn: boolean;
}

const NewHomesLandingPagePn: React.FC<NewHomesLandingPagePnProps> = ({
  images,
  superfeatures,
  featured,
  totalViewings,
  loggedIn,
}) => {
  const theme = useTheme();
  const searchState = useSearchState();
  const properties = useSelector(selectProperties);
  const search = useSelector(selectSearch);
  const [showSearchPreview, setShowSearchPreview] = useState('');
  const { recentSearches, removeRecentSearch } = useRecentSearches();
  const handPickedContainer = useRef<HTMLDivElement>(null);

  const handleSubmit = async (text: string, filters: Filters) => {
    const url = await getNlpUrl(text, { ...filters, category: 'newhomes', saleType: 'sale' });

    if (url) {
      await router.push(url);
    }
  };

  const handleSearchSubmit = () => {
    handleSubmit(searchState.searchText, searchState.appFilters);
  };

  const handlePreviewSubmit = (polyIds: number[]) => {
    handleSubmit(showSearchPreview === 'drawSearch' ? MY_DRAWN_AREA : MY_TRAVEL_TIME_AREA, {
      ...searchState.appFilters,
      polyIds,
    });

    setShowSearchPreview('');
  };

  const onCloseToSearch = async (polyIds: number[]) => {
    await handleSubmit(CLOSE_TO_SEARCH, {
      ...searchState.appFilters,
      polyIds,
      intersection: true,
    });

    return true;
  };

  useEffect(() => {
    searchState.handleFilterChange('category')('newhomesForSale');
  }, []);

  useInView(handPickedContainer, { threshold: 0.1 }, () => {
    featured?.forEach((dev) => {
      trackGaEvent('hand_picked_impression', {
        page: 'New Homes Landing',
        location: 'Hand Picked Developments',
        account_number: dev.account.accountNumber,
        development_ID: dev.id,
      });
    });
  });

  return (
    <div>
      <SearchBar
        onSearchModalToggle={() => {}}
        imageLocator={IMAGE_LOCATOR}
        loading={properties.loading}
        searchState={searchState}
        onSubmit={handleSearchSubmit}
        onDrawSearchClick={() => setShowSearchPreview('drawSearch')}
        onTravelTimeClick={() => setShowSearchPreview('travelTime')}
        loggedIn={loggedIn}
        onCloseToSearch={onCloseToSearch}
        recentSearches={recentSearches}
        removeRecentSearch={removeRecentSearch}
      />
      {showSearchPreview && (
        <SearchPreviewModal
          show={showSearchPreview}
          onClose={() => setShowSearchPreview('')}
          onSubmit={handlePreviewSubmit}
          initialPolyIds={search.filters.polyIds}
        />
      )}
      <ImageSlideshow images={images || []} slideDuration={10000} superFeatureProperties={superfeatures} />

      <HandPickedContainer ref={handPickedContainer}>
        <PopularPropertiesHome
          title="Hand picked new developments"
          properties={featured}
          link="/new-developments"
          linkText="View all new developments"
          maxLength={6}
          imageLocator={IMAGE_LOCATOR_XLARGE}
          photoScroller
          handPicked
          onPropertyClick={(p) => {
            trackGaEvent('hand_picked_click', {
              page: 'New Homes Landing',
              location: 'Hand Picked Developments',
              account_number: p.account.accountNumber,
              development_ID: p.id,
            });
          }}
        />
      </HandPickedContainer>

      <NewHomesContent>
        <ShowHomeWrapper>
          <ShowHomeContent>
            <Box flex="1">
              <Text as="h2" fontSize={25} mb={totalViewings ? 15 : undefined} fontWeight="bold" color={theme.textDark}>
                Show Home Opening Times
              </Text>

              {!!totalViewings && (
                <ShowHomeViewings data-testid="show-home-viewings">
                  <FontAwesomeIcon icon={faAlarmClock} size={30} />
                  <h3>
                    There {puralise('is', 'are', totalViewings)}{' '}
                    <b style={{ fontWeight: 900 }}>
                      {totalViewings} show {puralise('home', 'homes', totalViewings)}
                    </b>{' '}
                    open today.
                  </h3>
                </ShowHomeViewings>
              )}
            </Box>
            <Link href="/show-home-opening-times" style={{ maxWidth: 305, width: '100%' }}>
              <Button containerStyle={{ maxWidth: 305, padding: '0 10px', width: '100%' }}>
                View All Show Home Opening Times
              </Button>
            </Link>
          </ShowHomeContent>
        </ShowHomeWrapper>
      </NewHomesContent>

      <NewHomesContent>
        <SubHeading fontSize={25}>Thinking of buying a new home?</SubHeading>

        <MidHeading fontSize={17} fontWeight="normal">
          Here&apos;s our tips and advice when thinking about buying new.
        </MidHeading>

        <Benefits>
          <Tip>
            <FontAwesomeIcon icon={faHeart} size={24} />
            <h4>Peace of mind</h4>
            <p>Buying a new home means your protected by a 10 year warranty from NHBC.</p>
          </Tip>

          <Tip>
            <FontAwesomeIcon icon={faUser} size={24} />
            <h4>Personal to you</h4>
            <p>With a new home you get to personalise it to your specification with bespoke options.</p>
          </Tip>

          <Tip>
            <FontAwesomeIcon icon={faCircleInfo} size={24} />
            <h4>Help for buyers</h4>
            <p>
              Help to buy schemes along with first time buyer incentives on new homes make it easier to get on the
              property ladder.
            </p>
          </Tip>

          <Tip>
            <FontAwesomeIcon icon={faTruckFast} size={24} />
            <h4>Move in sooner</h4>
            <p>
              No chain means that as soon as the house is completed you can move in. All you have to think about is
              decorating!
            </p>
          </Tip>

          <Tip>
            <FontAwesomeIcon icon={faThumbsUp} size={24} />
            <h4>Get the best deal</h4>
            <p>
              New homebuilders offer a range of incentives for buyers that you&apos;ll never get from the second market.
            </p>
          </Tip>

          <Tip>
            <FontAwesomeIcon icon={faChartSimpleHorizontal} size={24} />
            <h4>Energy Efficient</h4>
            <p>A brand new home can be up to 60% more efficient than an older property.</p>
          </Tip>
        </Benefits>
      </NewHomesContent>
    </div>
  );
};

export default NewHomesLandingPagePn;
